import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type MutableRefObject,
  type ReactNode,
  type RefObject
} from 'react';
import { Document, Page } from 'react-pdf';
import type { DocumentCallback } from 'react-pdf/dist/shared/types.js';
import {
  AnchorCommentTextarea,
  hasIncompleteMention,
  validateAnchorCommentText
} from '@jarvis/platform-file-review';
import { apiFetch, readErrorMessage } from '../api/client';
import { FloatingCommentDialog } from '../fileReview/FloatingCommentDialog';
import {
  capturePdfSelection,
  findPdfHighlightAtPoint,
  type PdfCommentOnSelectionResult
} from './pdfSelection';
import type { PdfHighlightArea, PdfReviewAnchor } from './pdfViewerTypes';
import { PDF_DOCUMENT_OPTIONS } from './pdfJsConfig';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './JarvisPdfViewer.css';

const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;
const SCALE_STEP = 0.25;

export interface JarvisPdfHighlight {
  id: string;
  areas: PdfHighlightArea[];
  selectedText: string;
  comment?: string;
}

export interface JarvisPdfViewerProps {
  fileUrl: string;
  mountId?: string;
  /** File path within the mount; scopes WebOps parent mounts to the covering client folder. */
  path?: string;
  readOnly?: boolean;
  highlights?: JarvisPdfHighlight[];
  anchors?: PdfReviewAnchor[];
  activeAnchorId?: string | null;
  /**
   * Monotonically increasing token bumped by the parent on every explicit
   * "jump to highlight" request. Changing this re-triggers the scroll even
   * when `activeAnchorId` is unchanged (repeat clicks on the same discussion).
   */
  jumpRequest?: number;
  onHighlightCommitted?: (payload: {
    areas: PdfHighlightArea[];
    selectedText: string;
    comment: string;
  }) => void | Promise<void>;
  onHighlightClick?: (anchorId: string) => void;
  onCommentValidationError?: (message: string) => void;
  onDocumentLoad?: () => void;
  /** Called when download or PDF.js parsing fails; detail is safe to show in the UI. */
  onDocumentError?: (detail?: string, status?: number) => void;
  toolbarActions?: ReactNode;
}

export interface JarvisPdfViewerHandle {
  /**
   * Opens the selection-comment dialog for the current PDF text selection.
   * Returns a result describing whether the dialog opened or why it could not.
   */
  commentOnSelection: () => PdfCommentOnSelectionResult;
}

function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/** PDF.js transfers TypedArray buffers to the worker; clone per mount so remounts do not reuse detached memory. */
function pdfFileFromBuffer(sourceBuffer: ArrayBuffer): { data: Uint8Array } {
  return { data: new Uint8Array(sourceBuffer.slice(0)) };
}

interface JarvisPdfDocumentProps {
  sourceBuffer: ArrayBuffer;
  loadDetail: string | null;
  numPages: number;
  pageWidth: number;
  activeAnchorId: string | null;
  safeHighlights: JarvisPdfHighlight[];
  pageRefs: MutableRefObject<Map<number, HTMLDivElement>>;
  pagesRootRef: RefObject<HTMLDivElement | null>;
  onHighlightClick?: (anchorId: string) => void;
  onDocumentLoad: (pdf: DocumentCallback) => void;
  onDocumentError: (error: unknown) => void;
  onVisiblePageChange: (pageNumber: number) => void;
  onTextContent: (pageNumber: number, hasText: boolean) => void;
  onTextLayerError: (error: unknown) => void;
}

function JarvisPdfDocument({
  sourceBuffer,
  loadDetail,
  numPages,
  pageWidth,
  activeAnchorId,
  safeHighlights,
  pageRefs,
  pagesRootRef,
  onHighlightClick,
  onDocumentLoad,
  onDocumentError,
  onVisiblePageChange,
  onTextContent,
  onTextLayerError
}: JarvisPdfDocumentProps) {
  const file = useMemo(() => pdfFileFromBuffer(sourceBuffer), [sourceBuffer]);

  return (
    <Document
      file={file}
      loading={null}
      error={
        <div className="jarvis-pdf-viewer__error" role="alert">
          {loadDetail ?? 'Could not load this PDF.'}
        </div>
      }
      options={PDF_DOCUMENT_OPTIONS}
      onLoadSuccess={onDocumentLoad}
      onLoadError={onDocumentError}
      externalLinkTarget="_blank"
    >
      {Array.from({ length: numPages }, (_, idx) => {
        const pageIndex = idx;
        const pageNumber = pageIndex + 1;
        return (
          <div
            key={pageNumber}
            className="jarvis-pdf-page"
            data-page-index={pageIndex}
            ref={(el) => {
              if (el) pageRefs.current.set(pageIndex, el);
              else pageRefs.current.delete(pageIndex);
            }}
            onClick={(event) => {
              if (!onHighlightClick) return;
              const selection = window.getSelection();
              if (selection && !selection.isCollapsed) return;
              const pageRect = event.currentTarget.getBoundingClientRect();
              if (!pageRect.width || !pageRect.height) return;
              const highlightId = findPdfHighlightAtPoint(
                safeHighlights,
                pageIndex,
                ((event.clientX - pageRect.left) / pageRect.width) * 100,
                ((event.clientY - pageRect.top) / pageRect.height) * 100
              );
              if (highlightId) onHighlightClick(highlightId);
            }}
          >
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              loading={null}
              renderTextLayer
              onGetTextSuccess={({ items }) => {
                onTextContent(
                  pageNumber,
                  items.some((item) => 'str' in item && item.str.trim().length > 0)
                );
              }}
              onGetTextError={onTextLayerError}
              onRenderTextLayerError={onTextLayerError}
              onRenderSuccess={() => {
                const root = pagesRootRef.current;
                const pageEl = pageRefs.current.get(pageIndex);
                if (!root || !pageEl) return;
                const rootRect = root.getBoundingClientRect();
                const pageRect = pageEl.getBoundingClientRect();
                if (pageRect.top <= rootRect.top + 120 && pageRect.bottom >= rootRect.top + 120) {
                  onVisiblePageChange(pageNumber);
                }
              }}
            />
            <div className="jarvis-pdf-highlight-layer" aria-hidden="true">
              {safeHighlights.flatMap((h) =>
                h.areas
                  .filter((area) => area.pageIndex === pageIndex)
                  .map((area, areaIndex) => {
                    const isActive = h.id === activeAnchorId;
                    return (
                      <div
                        key={`${h.id}-${areaIndex}`}
                        className={`jarvis-pdf-highlight-overlay${isActive ? ' jarvis-pdf-highlight-overlay--active' : ''}`}
                        style={{
                          left: `${area.left}%`,
                          top: `${area.top}%`,
                          width: `${area.width}%`,
                          height: `${area.height}%`
                        }}
                        title={h.comment || h.selectedText}
                      >
                        {h.comment ? (
                          <span className="jarvis-pdf-highlight-overlay__label">{h.comment}</span>
                        ) : null}
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        );
      })}
    </Document>
  );
}

export const JarvisPdfViewer = forwardRef<JarvisPdfViewerHandle, JarvisPdfViewerProps>(
  function JarvisPdfViewer(
    {
      fileUrl,
      mountId,
      path,
      readOnly = false,
      highlights = [],
      anchors = [],
      activeAnchorId = null,
      jumpRequest = 0,
      onHighlightCommitted,
      onHighlightClick,
      onCommentValidationError,
      onDocumentLoad,
      onDocumentError,
      toolbarActions
    },
    ref
  ) {
  const [numPages, setNumPages] = useState(0);
  const [visiblePage, setVisiblePage] = useState(1);
  const [scale, setScale] = useState(1);
  const [viewerWidth, setViewerWidth] = useState(900);
  const [pdfSource, setPdfSource] = useState<ArrayBuffer | null>(null);
  const [fetching, setFetching] = useState(false);
  const [loadDetail, setLoadDetail] = useState<string | null>(null);
  const [textLayerState, setTextLayerState] =
    useState<'unknown' | 'available' | 'unavailable' | 'error'>('unknown');
  const textContentByPageRef = useRef(new Map<number, boolean>());
  const onDocumentLoadRef = useRef(onDocumentLoad);
  const onDocumentErrorRef = useRef(onDocumentError);
  onDocumentLoadRef.current = onDocumentLoad;
  onDocumentErrorRef.current = onDocumentError;
  const safeHighlights = useMemo(
    () =>
      (Array.isArray(highlights) ? highlights : []).map((h) => ({
        ...h,
        areas: Array.isArray(h.areas) ? h.areas : []
      })),
    [highlights]
  );
  const safeAnchors = useMemo(
    () =>
      (Array.isArray(anchors) ? anchors : []).map((a) => ({
        ...a,
        areas: Array.isArray(a.areas) ? a.areas : []
      })),
    [anchors]
  );

  const viewerRootRef = useRef<HTMLDivElement>(null);
  const pagesRootRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef(new Map<number, HTMLDivElement>());
  const pendingSelectionRef = useRef<{
    areas: PdfHighlightArea[];
    selectedText: string;
  } | null>(null);
  const [contextComment, setContextComment] = useState<{
    areas: PdfHighlightArea[];
    selectedText: string;
  } | null>(null);
  const [contextNoteDraft, setContextNoteDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  useEffect(() => {
    const root = pagesRootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 900;
      setViewerWidth(Math.max(320, width - 48));
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setNumPages(0);
    setVisiblePage(1);
    setContextComment(null);
    setContextNoteDraft('');
    setPdfSource(null);
    setLoadDetail(null);
    setTextLayerState('unknown');
    textContentByPageRef.current.clear();
    pendingSelectionRef.current = null;
  }, [fileUrl]);

  useEffect(() => {
    if (!fileUrl) {
      setFetching(false);
      return;
    }
    let cancelled = false;
    setFetching(true);
    setPdfSource(null);
    setLoadDetail(null);

    void (async () => {
      try {
        const res = await apiFetch(fileUrl);
        if (!res.ok) {
          const msg = await readErrorMessage(res);
          const detail =
            res.status === 403
              ? `You do not have permission to view this file. ${msg}`
              : res.status === 404
                ? `File not found. ${msg}`
                : msg;
          if (!cancelled) {
            setLoadDetail(detail);
            onDocumentErrorRef.current?.(detail, res.status);
          }
          return;
        }
        const buf = await res.arrayBuffer();
        if (!buf.byteLength) {
          const detail = 'The file is empty.';
          if (!cancelled) {
            setLoadDetail(detail);
            onDocumentErrorRef.current?.(detail);
          }
          return;
        }
        if (!cancelled) setPdfSource(buf);
      } catch (err) {
        const detail =
          err instanceof Error ? err.message : 'Failed to download the PDF.';
        if (!cancelled) {
          setLoadDetail(detail);
          onDocumentErrorRef.current?.(detail);
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  /**
   * Scroll on each explicit jump request; do not re-scroll when the thread or
   * highlights merely refresh. Keying on `jumpRequest` (not `activeAnchorId`)
   * lets repeat clicks on the same discussion navigate every time (ITB-1650),
   * while a refresh that changes `safeAnchors`/`safeHighlights` without a new
   * jump request is ignored because `jumpRequest` is unchanged.
   */
  const lastJumpRequestRef = useRef<number>(jumpRequest);

  useEffect(() => {
    if (!activeAnchorId) {
      lastJumpRequestRef.current = jumpRequest;
      return;
    }
    if (lastJumpRequestRef.current === jumpRequest) return;

    const anchor = safeAnchors.find((a) => a.id === activeAnchorId);
    const hl = safeHighlights.find((h) => h.id === activeAnchorId);
    const areas = anchor?.areas ?? hl?.areas;
    // Keep the request pending until the target's areas are available so a jump
    // triggered before the thread finished loading still scrolls once ready.
    if (!areas?.length) return;

    const firstArea = areas[0];
    const pageEl = pageRefs.current.get(firstArea.pageIndex);
    pageEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    lastJumpRequestRef.current = jumpRequest;
  }, [jumpRequest, activeAnchorId, safeAnchors, safeHighlights]);

  // Record-only: never clear the cache here. Ribbon clicks (Safari especially) can
  // empty window.getSelection() before commentOnSelection runs; wiping the cache
  // would make the pendingSelectionRef fallback dead. Clearing happens in the
  // viewer-root mouseup listener when the user deliberately deselects in-PDF.
  const captureSelection = useCallback(() => {
    const captured = capturePdfSelection(window.getSelection(), pageRefs.current.values());
    if (captured.status === 'none') {
      return null;
    }
    if (captured.status === 'unmapped') {
      const pending = { areas: [] as PdfHighlightArea[], selectedText: captured.selectedText };
      pendingSelectionRef.current = pending;
      return pending;
    }
    const pending = {
      selectedText: captured.selectedText,
      areas: captured.areas
    };
    pendingSelectionRef.current = pending;
    return pending;
  }, []);

  const openCommentOnSelection = useCallback(() => {
    if (readOnly) return 'read-only';
    const captured = captureSelection();
    const pending = captured ?? pendingSelectionRef.current;
    if (!pending?.selectedText) {
      if (textLayerState === 'unavailable') return 'no-selectable-text';
      if (textLayerState === 'error') return 'text-layer-error';
      return 'no-selection';
    }
    if (!(pending.areas?.length ?? 0)) return 'selection-unmapped';

    setContextComment({
      areas: pending.areas,
      selectedText: pending.selectedText
    });
    setContextNoteDraft('');
    setCommentSaving(false);
    return 'opened';
  }, [captureSelection, readOnly, textLayerState]);

  const closeCommentOnSelection = useCallback(() => {
    setContextComment(null);
    setContextNoteDraft('');
    setCommentSaving(false);
    pendingSelectionRef.current = null;
  }, []);

  const saveCommentOnSelection = useCallback(async () => {
    if (!contextComment || !onHighlightCommitted || commentSaving) return;
    const comment = contextNoteDraft.trim();
    if (!comment || hasIncompleteMention(contextNoteDraft)) return;
    const validationError = validateAnchorCommentText(comment);
    if (validationError) {
      onCommentValidationError?.(validationError);
      return;
    }
    setCommentSaving(true);
    try {
      await onHighlightCommitted({
        areas: contextComment.areas,
        selectedText: contextComment.selectedText,
        comment
      });
      closeCommentOnSelection();
    } finally {
      setCommentSaving(false);
    }
  }, [
    closeCommentOnSelection,
    commentSaving,
    contextComment,
    contextNoteDraft,
    onCommentValidationError,
    onHighlightCommitted
  ]);

  useImperativeHandle(
    ref,
    () => ({
      commentOnSelection: () => openCommentOnSelection()
    }),
    [openCommentOnSelection]
  );

  useEffect(() => {
    const root = viewerRootRef.current;
    if (!root || readOnly) return;

    const onMouseUp = () => {
      if (!captureSelection()) {
        pendingSelectionRef.current = null;
      }
    };

    const onContextMenu = (e: MouseEvent) => {
      if (!openCommentOnSelection()) return;
      e.preventDefault();
    };

    root.addEventListener('mouseup', onMouseUp);
    root.addEventListener('contextmenu', onContextMenu);
    return () => {
      root.removeEventListener('mouseup', onMouseUp);
      root.removeEventListener('contextmenu', onContextMenu);
    };
  }, [captureSelection, openCommentOnSelection, readOnly]);

  const handleDocumentLoad = useCallback((pdf: DocumentCallback) => {
    setNumPages(pdf.numPages);
    setLoadDetail(null);
    onDocumentLoadRef.current?.();
  }, []);

  const handleTextContent = useCallback(
    (pageNumber: number, hasText: boolean) => {
      textContentByPageRef.current.set(pageNumber, hasText);
      if (hasText) {
        setTextLayerState('available');
      } else if (
        numPages > 0 &&
        textContentByPageRef.current.size >= numPages &&
        !Array.from(textContentByPageRef.current.values()).some(Boolean)
      ) {
        setTextLayerState('unavailable');
      }
    },
    [numPages]
  );

  const handleTextLayerError = useCallback((error: unknown) => {
    console.error('[jarvis-pdf-viewer] text layer failed', error);
    setTextLayerState('error');
  }, []);

  const handleDocumentError = useCallback((error: unknown) => {
    const detail =
      error instanceof Error && error.message.trim()
        ? error.message.trim()
        : 'Could not parse this PDF. It may be corrupted or unsupported.';
    setLoadDetail(detail);
    onDocumentErrorRef.current?.(detail);
  }, []);

  const goToPage = useCallback((pageNumber: number) => {
    const target = Math.min(Math.max(pageNumber, 1), Math.max(numPages, 1));
    pageRefs.current.get(target - 1)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setVisiblePage(target);
  }, [numPages]);

  const pageWidth = Math.min(viewerWidth, 1120) * scale;

  return (
    <div className="jarvis-pdf-viewer" ref={viewerRootRef}>
      <div className="jarvis-pdf-viewer__toolbar" aria-label="PDF controls">
        <div className="jarvis-pdf-viewer__toolbar-nav">
          <button
            type="button"
            className="jarvis-pdf-viewer__toolbar-button"
            disabled={visiblePage <= 1}
            title="Previous page"
            aria-label="Previous page"
            onClick={() => goToPage(visiblePage - 1)}
          >
            {'<'}
          </button>
          <span className="jarvis-pdf-viewer__toolbar-status">
            Page {numPages ? visiblePage : 0} / {numPages || 0}
          </span>
          <button
            type="button"
            className="jarvis-pdf-viewer__toolbar-button"
            disabled={!numPages || visiblePage >= numPages}
            title="Next page"
            aria-label="Next page"
            onClick={() => goToPage(visiblePage + 1)}
          >
            {'>'}
          </button>
          <span className="jarvis-pdf-viewer__toolbar-divider" />
          <button
            type="button"
            className="jarvis-pdf-viewer__toolbar-button"
            disabled={scale <= MIN_SCALE}
            title="Zoom out"
            aria-label="Zoom out"
            onClick={() => setScale((v) => clampScale(v - SCALE_STEP))}
          >
            -
          </button>
          <span className="jarvis-pdf-viewer__toolbar-status">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            className="jarvis-pdf-viewer__toolbar-button"
            disabled={scale >= MAX_SCALE}
            title="Zoom in"
            aria-label="Zoom in"
            onClick={() => setScale((v) => clampScale(v + SCALE_STEP))}
          >
            +
          </button>
        </div>
        {toolbarActions ? <div className="jarvis-pdf-viewer__toolbar-actions">{toolbarActions}</div> : null}
      </div>

      <div className="jarvis-pdf-viewer__document" ref={pagesRootRef}>
        {fetching ? (
          <div className="jarvis-pdf-viewer__loading" aria-busy="true">
            Loading PDF…
          </div>
        ) : null}
        {loadDetail && !pdfSource ? (
          <div className="jarvis-pdf-viewer__error" role="alert">
            {loadDetail}
          </div>
        ) : null}
        {pdfSource ? (
          <JarvisPdfDocument
            key={fileUrl}
            sourceBuffer={pdfSource}
            loadDetail={loadDetail}
            numPages={numPages}
            pageWidth={pageWidth}
            activeAnchorId={activeAnchorId}
            safeHighlights={safeHighlights}
            pageRefs={pageRefs}
            pagesRootRef={pagesRootRef}
            onHighlightClick={onHighlightClick}
            onDocumentLoad={handleDocumentLoad}
            onDocumentError={handleDocumentError}
            onVisiblePageChange={setVisiblePage}
            onTextContent={handleTextContent}
            onTextLayerError={handleTextLayerError}
          />
        ) : null}
      </div>

      {contextComment ? (
        <FloatingCommentDialog
          ariaLabel="Comment on selection"
          dialogClassName="jarvis-pdf-selection-comment"
        >
          <AnchorCommentTextarea
            mountId={mountId}
            path={path}
            className="jarvis-pdf-highlight-popover__input"
            rows={9}
            placeholder="Comment on selection… Type @ to pick someone to notify"
            value={contextNoteDraft}
            disabled={commentSaving}
            autoFocus
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setContextNoteDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                closeCommentOnSelection();
              }
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void saveCommentOnSelection();
              }
            }}
          />
          <div className="jarvis-pdf-highlight-popover__actions">
            <button
              type="button"
              className="jarvis-pdf-highlight-popover__cancel"
              disabled={commentSaving}
              onClick={closeCommentOnSelection}
            >
              Cancel
            </button>
            <button
              type="button"
              className="jarvis-pdf-highlight-popover__save"
              disabled={
                commentSaving || !contextNoteDraft.trim() || hasIncompleteMention(contextNoteDraft)
              }
              onClick={() => void saveCommentOnSelection()}
            >
              Save
            </button>
          </div>
        </FloatingCommentDialog>
      ) : null}
    </div>
  );
});
